import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { MemorialCard } from "./Celebrate";
import { Filter } from "lucide-react";
import { useCountryFilter } from "@/lib/useCountryFilter";
import CountryFilterBanner from "@/components/CountryFilterBanner";

export default function CelebrateBrowse() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const filtered = useCountryFilter(items, ["birth_place", "death_place", "nationality"]);

  const q = params.get("q") || "";
  const year = params.get("year") || "";
  const location = params.get("location") || "";
  const profession = params.get("profession") || "";
  const sort = params.get("sort") || "recent";
  const anniv = params.get("anniversary") === "today";

  useEffect(() => {
    http.get("/memorials/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const url = anniv ? "/memorials/today-anniversaries" : "/memorials";
    const qs = anniv ? {} : { q, year: year || undefined, location: location || undefined, profession: profession || undefined, sort };
    http.get(url, { params: qs }).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || (r.data.items || []).length);
    }).finally(() => setLoading(false));
  }, [q, year, location, profession, sort, anniv]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (!v) next.delete(k);
      else next.set(k, v);
    });
    setParams(next);
  };

  return (
    <div data-testid="celebrate-browse" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>CELEBRATE A LIFE</div>
      <h1 className="font-display text-4xl md:text-5xl">Browse the archive</h1>

      {/* Filters */}
      <div className="mt-8 wofy-card">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
          <span className="text-xs font-mono uppercase tracking-wider">Filters</span>
        </div>
        <div className="grid md:grid-cols-5 gap-3">
          <input data-testid="cb-q" placeholder="Name / bio / place…" value={q} onChange={(e) => update({ q: e.target.value })} className="rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          <input data-testid="cb-year" placeholder="Year" value={year} onChange={(e) => update({ year: e.target.value })} className="rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          <input data-testid="cb-location" placeholder="Location" value={location} onChange={(e) => update({ location: e.target.value })} className="rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          <select data-testid="cb-profession" value={profession} onChange={(e) => update({ profession: e.target.value })} className="rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }}>
            <option value="">All professions</option>
            {config?.professions?.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="cb-sort" value={sort} onChange={(e) => update({ sort: e.target.value })} className="rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }}>
            <option value="recent">Most recent</option>
            <option value="name">By name</option>
          </select>
        </div>
      </div>

      <div className="mt-6 text-sm font-mono" style={{ color: "var(--wofy-text-soft)" }}>{loading ? "Loading…" : `${filtered.length} memorial${filtered.length === 1 ? "" : "s"}`}</div>

      <CountryFilterBanner shown={filtered.length} total={items.length} noun="memorials" />

      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((m) => <MemorialCard key={m.id} m={m} />)}
      </div>
    </div>
  );
}
